import { useTransition, useState, useEffect, useRef } from "react";

import { EditIcon, Divider, SearchField, Tooltip } from "@stacklet/ui";
import { graphql, usePaginationFragment } from "react-relay";

import { useIsTruncated } from "app/hooks";
import { usePollingRefetchable } from "app/hooks/relay/usePollingRefetchable";
import { createSingleFilter } from "app/utils/filters";

import type { ChatThreadsList_query$key } from "./__generated__/ChatThreadsList_query.graphql";
import type { ChatThreadsListRefetchQuery } from "./__generated__/ChatThreadsListRefetchQuery.graphql";

const CHAT_HISTORY_PAGE_SIZE = 25;

interface Props {
  queryRef: ChatThreadsList_query$key;
  onNewChat?: () => void;
  onThreadClick?: (uuid: string) => void;
}

interface Thread {
  id: string;
  uuid: string;
  name: string | null;
  created: string;
}

interface ThreadGroup {
  label: string;
  threads: Thread[];
}

function groupThreadsByDate(
  threads: (Omit<Thread, "name"> & { name: string | null | undefined })[],
): ThreadGroup[] {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(
    startOfToday.getDate() - ((startOfToday.getDay() + 6) % 7),
  );
  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setDate(startOfWeek.getDate() - 7);

  const groups: ThreadGroup[] = [
    { label: "Today", threads: [] },
    { label: "This Week", threads: [] },
    { label: "Last Week", threads: [] },
    { label: "A While Back", threads: [] },
  ];

  for (const thread of threads) {
    const created = new Date(thread.created);
    const entry: Thread = {
      id: thread.id,
      uuid: thread.uuid,
      name: thread.name ?? null,
      created: thread.created,
    };
    if (created >= startOfToday) {
      groups[0].threads.push(entry);
    } else if (created >= startOfWeek) {
      groups[1].threads.push(entry);
    } else if (created >= startOfLastWeek) {
      groups[2].threads.push(entry);
    } else {
      groups[3].threads.push(entry);
    }
  }

  return groups.filter((g) => g.threads.length > 0);
}

function ThreadButton({
  name,
  onClick,
}: {
  name: string;
  onClick?: () => void;
}) {
  const [textRef, isTruncated] = useIsTruncated<HTMLSpanElement>();

  return (
    <Tooltip
      buttonProps={{
        className:
          "w-full px-xl py-sm text-left text-label-medium text-text-secondary transition-colors hover:bg-background-tertiary hover:text-text-primary",
        onPress: onClick,
      }}
      className="ml-1"
      isDisabled={!isTruncated}
      offset={0}
      placement="right"
      title={name}
    >
      <span className="block truncate" ref={textRef}>
        {name}
      </span>
    </Tooltip>
  );
}

export function ChatThreadsList({ queryRef, onNewChat, onThreadClick }: Props) {
  const [search, setSearch] = useState("");
  const [isPending, startTransition] = useTransition();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the active filterElement so polling refreshes with the same filter
  // that is currently applied, rather than dropping back to the unfiltered
  // list. State (not a ref): usePollingRefetchable tracks variables internally
  // so updating this doesn't restart the polling interval.
  const [activeFilter, setActiveFilter] = useState<Record<string, unknown>>({});

  const { data, refetch } = usePaginationFragment<
    ChatThreadsListRefetchQuery,
    ChatThreadsList_query$key
  >(
    graphql`
      fragment ChatThreadsList_query on Query
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 25 }
        after: { type: "String" }
        filterElement: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "ChatThreadsListRefetchQuery") {
        jun0Threads(
          first: $first
          after: $after
          filterElement: $filterElement
        ) @connection(key: "ChatThreadsList_jun0Threads") {
          edges {
            node {
              id
              uuid
              name
              created
            }
          }
        }
      }
    `,
    queryRef,
  );

  usePollingRefetchable(refetch, {
    interval: 10_000,
    variables: activeFilter,
  });

  function handleSearch(value: string) {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const filterElement = value.trim()
        ? (createSingleFilter("name", value.trim(), "contains") ?? undefined)
        : undefined;
      setActiveFilter(
        filterElement ? { first: CHAT_HISTORY_PAGE_SIZE, filterElement } : {},
      );
      startTransition(() => {
        refetch({ first: CHAT_HISTORY_PAGE_SIZE, filterElement });
      });
    }, 300);
  }

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const threads = (data.jun0Threads?.edges ?? [])
    .map((edge) => edge.node)
    .filter((n): n is NonNullable<typeof n> => n !== null);

  const groups = groupThreadsByDate(threads);

  return (
    <>
      <div className="px-xs pb-sm pt-lg">
        <SearchField
          className="w-full"
          inputProps={{ placeholder: "Search…" }}
          isPending={isPending}
          onChange={handleSearch}
          value={search}
        />
      </div>
      <div className="px-xs">
        <Divider />
      </div>

      <button
        className="flex w-full items-center justify-between px-xl pb-sm pt-lg text-label-medium text-text-secondary transition-colors hover:bg-background-tertiary hover:text-text-primary"
        onClick={onNewChat}
        type="button"
      >
        <span>New Chat</span>
        <EditIcon size={16} />
      </button>

      <div className={isPending ? "opacity-50 transition-opacity" : undefined}>
        {threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-xl py-2xl text-center">
            <p className="text-label-medium text-text-secondary">
              {search ? "No matching conversations" : "No conversations yet"}
            </p>
          </div>
        ) : (
          <div className="flex flex-col pb-lg">
            {groups.map((group) => (
              <div key={group.label}>
                <div className="px-xl pb-xs pt-lg">
                  <span className="text-label-extra-small font-semibold uppercase tracking-wide text-text-secondary">
                    {group.label}
                  </span>
                </div>
                <ul>
                  {group.threads.map((thread) => (
                    <li key={thread.id}>
                      <ThreadButton
                        name={thread.name ?? "Untitled"}
                        onClick={() => onThreadClick?.(thread.uuid)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
